import { Form, Space, Divider, Typography, ColorPicker, Row, Col, Input } from 'antd';
import { InfoCircleOutlined } from '@ant-design/icons';
import CustomTooltip from '../../Components/Modals/CustomTooltip';
import BorderRadiusControl from '../BorderRadiusControl';
import Tutorial from './Tutorial';

const { Title, Text } = Typography;
const InfoIcon = InfoCircleOutlined;

const CustomizationStep = ({
    form,
    formData,
    handleFormChange,
    isDomainClean
}) => {
    return (
        <Row gutter={32} style={{ minHeight: 400 }}>
            {/* Left: Form */}
            <Col xs={24} md={12}>
                <div style={{ padding: '1.25rem 1rem' }}>
                    <Title level={4}>Catalogue Information & Customization</Title>
                    <Text type="secondary">
                        Enter basic information and customize the appearance of your catalogue.
                    </Text>

                    <Form
                        form={form}
                        layout="vertical"
                        style={{ marginTop: '1.5rem' }}
                        onValuesChange={handleFormChange}
                    >
                        <Space direction="vertical" style={{ width: '100%' }}>
                            <Divider orientation="left">Basic Information</Divider>

                            <Form.Item
                                name="catalogueName"
                                label="Catalogue Name"
                                hasFeedback
                                rules={[
                                    { required: true, message: 'Please enter a catalogue name' },
                                    { min: 3, message: 'Catalogue name must be at least 3 characters' },
                                    {
                                        validator: (_, value) => {
                                            if (!value) return Promise.resolve();
                                            if (!isDomainClean(value)) {
                                                return Promise.reject(new Error('Catalogue name contains inappropriate words'));
                                            }
                                            return Promise.resolve();
                                        },
                                        validateTrigger: ['onChange', 'onBlur']
                                    }
                                ]}
                            >
                                <Input
                                    placeholder="Enter catalogue name"
                                    onChange={(e) => {
                                        const value = e.target.value;
                                        if (value) {
                                            form.validateFields(['catalogueName']);
                                        }
                                    }}
                                />
                            </Form.Item>

                            <Divider orientation="left">Colors</Divider>

                            <Form.Item
                                name={['customization', 'primaryColor']}
                                label={<>
                                    Primary Color (VTO Button)
                                    <CustomTooltip title="This color will be used for the VTO button background">
                                        <InfoIcon style={{ fontSize: 16, marginLeft: 5, color: '#999' }} />
                                    </CustomTooltip>
                                </>}
                            >
                                <ColorPicker />
                            </Form.Item>

                            <Form.Item
                                name={['customization', 'secondaryColor']}
                                label={<>
                                    Secondary Color (3D Button)
                                    <CustomTooltip title="This color will be used for the 3D button background">
                                        <InfoIcon style={{ fontSize: 16, marginLeft: 5, color: '#999' }} />
                                    </CustomTooltip>
                                </>}
                            >
                                <ColorPicker />
                            </Form.Item>

                            <Form.Item
                                name={['customization', 'backgroundColor']}
                                label={<>
                                    Background Color
                                    <CustomTooltip title="This color will be used for the catalog background">
                                        <InfoIcon style={{ fontSize: 16, marginLeft: 5, color: '#999' }} />
                                    </CustomTooltip>
                                </>}
                            >
                                <ColorPicker />
                            </Form.Item>

                            <Divider orientation="left">Corners</Divider>

                            <Form.Item>
                                <label style={{ display: 'block', marginBottom: 8 }}>
                                    Card Corner Radius
                                    <CustomTooltip title="Set the border radius for the corners of the product cards">
                                        <InfoIcon style={{ fontSize: 16, marginLeft: 5, color: '#999' }} />
                                    </CustomTooltip>
                                </label>
                                <BorderRadiusControl
                                    prefix="card"
                                    topLeft={formData.customization.cardCornerTopLeft}
                                    topRight={formData.customization.cardCornerTopRight}
                                    bottomRight={formData.customization.cardCornerBottomRight}
                                    bottomLeft={formData.customization.cardCornerBottomLeft}
                                    onChange={(values) => handleFormChange({ customization: values })}
                                />
                            </Form.Item>

                            <Form.Item>
                                <label style={{ display: 'block', marginBottom: 8 }}>
                                    Button Corner Radius
                                    <CustomTooltip title="Set the border radius for the corners of the action buttons">
                                        <InfoIcon style={{ fontSize: 16, marginLeft: 5, color: '#999' }} />
                                    </CustomTooltip>
                                </label>
                                <BorderRadiusControl
                                    prefix="button"
                                    topLeft={formData.customization.buttonCornerTopLeft}
                                    topRight={formData.customization.buttonCornerTopRight}
                                    bottomRight={formData.customization.buttonCornerBottomRight}
                                    bottomLeft={formData.customization.buttonCornerBottomLeft}
                                    onChange={(values) => handleFormChange({ customization: values })}
                                />
                            </Form.Item>

                        </Space>
                    </Form>
                </div>
            </Col>
            {/* Right: Tutorial */}
            <Col xs={24} md={12} style={{ display: 'flex', flexDirection: 'column', justifyContent: 'start' }}>
                <Tutorial
                    title="Customization"
                    description={
                        <>
                            Personalize the look & feel of your catalogue.<br />
                            Here you can change the main colors, background, and the corner radius of banners, cards, and buttons to match your brand identity.<br /><br />
                            <b>Tips:</b>
                            <ul style={{ paddingLeft: 20, margin: 0 }}>
                                <li>Use your brand's primary and secondary colors for a consistent experience</li>
                                <li>Adjust corner radius for a modern or classic look</li>
                                <li>Preview changes to see their impact in real time</li>
                                <li>Choose colors that provide good contrast for accessibility</li>
                                <li>Consider how colors will appear on different devices and backgrounds</li>
                            </ul>
                        </>
                    }
                />
            </Col>
        </Row>
    );
}

export default CustomizationStep;
